import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import ReadingTimer from './ReadingTimer';

const API = process.env.REACT_APP_BACKEND_URL;

// Your reading journal for one novel: notes to your future self (talk into
// the mic, Whisper writes it down, a model tidies it into readable first
// person) and the highlights you made on your Kindle. Upload the notebook PDF
// your Kindle sends and each highlight is filed under its chapter; you can
// comment on any of them, typed or spoken. See it all by chapter, in story
// order, or as one timeline by date.

const STATUS_LABELS = {
  want: 'want to read',
  reading: 'reading',
  on_hold: 'on hold',
  finished: 'finished',
  dropped: 'dropped',
};

// the colours a Kindle highlight can have
const HIGHLIGHT_COLORS = {
  yellow: '#d9b45a',
  blue: '#6f9fd8',
  pink: '#d87fa6',
  orange: '#d98e4f',
};

// highlights are set in Palatino (built into iPhone, iPad and Mac; Windows
// calls it Palatino Linotype or Book Antiqua), falling back to the site's serif
const HIGHLIGHT_FONT = "Palatino, 'Palatino Linotype', 'Book Antiqua', 'Cormorant Garamond', serif";

const MicIcon = ({ size = 22, color = '#0e0b0f' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="2" width="6" height="12" rx="3" />
    <path d="M5 10v1a7 7 0 0 0 14 0v-1" />
    <path d="M12 18v4" />
  </svg>
);

const StopIcon = ({ size = 18, color = '#e8ddd0' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} stroke="none">
    <rect x="6" y="6" width="12" height="12" rx="2" />
  </svg>
);

const UploadIcon = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 16V4" /><path d="M7 9l5-5 5 5" /><path d="M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3" />
  </svg>
);

const QuoteIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M7 7h4v4c0 3-1.5 5-4 6" /><path d="M14 7h4v4c0 3-1.5 5-4 6" />
  </svg>
);

const NoteIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 4h14v12l-4 4H5z" /><path d="M15 20v-4h4" /><path d="M8 9h8M8 13h5" />
  </svg>
);

const CommentIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 5h16v11H9l-5 4z" />
  </svg>
);

// "2026-09" -> "Sep 2026"
const formatMonth = (m) => {
  if (!m) return '';
  const [y, mo] = m.split('-').map(Number);
  if (!y || !mo) return m;
  return new Date(y, mo - 1, 1).toLocaleDateString([], { month: 'short', year: 'numeric' });
};

// "2026-10-01" (a day with no time) is read as that day where you are, not as UTC midnight
const toDate = (iso) => (/^\d{4}-\d{2}-\d{2}$/.test(iso || '') ? new Date(`${iso}T12:00:00`) : new Date(iso));

const dayKey = (iso) => {
  const d = toDate(iso);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
};

const dayLabel = (iso) => {
  const d = toDate(iso);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  const same = (a, b) => a.toDateString() === b.toDateString();
  const full = d.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  if (same(d, today)) return `Today · ${full}`;
  if (same(d, yesterday)) return `Yesterday · ${full}`;
  return full;
};

const timeLabel = (iso) =>
  new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

const shortDate = (iso) =>
  toDate(iso).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });

const blobToBase64 = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onloadend = () => {
    const result = String(reader.result || '');
    resolve(result.includes(',') ? result.split(',')[1] : result);
  };
  reader.onerror = reject;
  reader.readAsDataURL(blob);
});

export default function NovelNotes() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const [novel, setNovel] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [notes, setNotes] = useState([]);
  const [highlights, setHighlights] = useState([]);
  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');

  // how the journal is shown
  const [view, setView] = useState(() => { try { return localStorage.getItem('journalView') || 'chapter'; } catch (e) { return 'chapter'; } });
  const [show, setShow] = useState('all');            // all | highlights | notes
  const [showImports, setShowImports] = useState(false);

  // uploading a Kindle notebook
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileRef = useRef(null);

  // the note being written
  const [draft, setDraft] = useState('');
  const [rawTranscript, setRawTranscript] = useState('');
  const [showDraftRaw, setShowDraftRaw] = useState(false);
  const [draftChapterId, setDraftChapterId] = useState('');
  const draftChapterRef = useRef('');
  const [savingNote, setSavingNote] = useState(false);
  const [tidyingDraft, setTidyingDraft] = useState(false);

  // a comment being written on a highlight
  const [commentOn, setCommentOn] = useState('');      // highlight id
  const [commentDraft, setCommentDraft] = useState('');
  const [commentRaw, setCommentRaw] = useState('');
  const [savingComment, setSavingComment] = useState(false);

  // recording: idle -> recording -> transcribing -> tidying -> idle. One
  // recorder serves both the note composer and the comment box; recTarget
  // says which one the words go into.
  const [recState, setRecState] = useState('idle');
  const [recSeconds, setRecSeconds] = useState(0);
  const [recTarget, setRecTarget] = useState('note');  // 'note' | highlight id
  const recTargetRef = useRef('note');
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);

  // editing a saved note
  const [editingId, setEditingId] = useState('');
  const [editDraft, setEditDraft] = useState('');
  const [editChapterId, setEditChapterId] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState('');
  const [openRawIds, setOpenRawIds] = useState([]);

  useEffect(() => {
    if (!token) { navigate('/'); return; }
    loadAll();
    return () => releaseMic();
  }, [id]);

  useEffect(() => { draftChapterRef.current = draftChapterId; }, [draftChapterId]);

  const pickView = (v) => {
    setView(v);
    try { localStorage.setItem('journalView', v); } catch (e) { /* fine */ }
  };

  const describeError = (err) => {
    if (err && err.response) {
      const d = err.response.data?.detail;
      const detailText = Array.isArray(d)
        ? d.map(x => `${(x.loc || []).join('.')}: ${x.msg}`).join(' | ')
        : (typeof d === 'string' ? d : JSON.stringify(d));
      return `${err.response.status} — ${detailText || 'no detail'}`;
    }
    return (err && err.message) || 'network error';
  };

  const loadAll = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/novels/${id}?token=${token}`),
      axios.get(`${API}/novels/${id}/chapters?token=${token}`),
      axios.get(`${API}/novels/${id}/notes?token=${token}`),
      axios.get(`${API}/novels/${id}/highlights?token=${token}`).catch(() => ({ data: { highlights: [], imports: [] } })),
    ])
      .then(([novelRes, chaptersRes, notesRes, hlRes]) => {
        const chs = chaptersRes.data.chapters || [];
        const ns = notesRes.data.notes || [];
        setNovel(novelRes.data.novel);
        setChapters(chs);
        setNotes(ns);
        setHighlights(hlRes.data.highlights || []);
        setImports(hlRes.data.imports || []);
        // start on the chapter your last note was about; failing that, the
        // furthest chapter that's been translated (roughly where you are)
        const lastNoteChapter = ns.find(n => n.chapter_id)?.chapter_id;
        const translated = chs.filter(c => c.is_translated);
        const furthest = translated.length ? translated[translated.length - 1].id : '';
        setDraftChapterId(lastNoteChapter && chs.some(c => c.id === lastNoteChapter)
          ? lastNoteChapter
          : furthest);
      })
      .catch(err => setErrorMsg(`couldn't load the journal: ${describeError(err)}`))
      .finally(() => setLoading(false));
  };

  const reloadHighlights = () =>
    axios.get(`${API}/novels/${id}/highlights?token=${token}`)
      .then(res => { setHighlights(res.data.highlights || []); setImports(res.data.imports || []); });

  // ---- uploading a Kindle notebook ----
  const onPickFile = (e) => {
    const file = (e.target.files || [])[0];
    e.target.value = '';
    if (!file) return;
    if (!/\.pdf$/i.test(file.name) && file.type !== 'application/pdf') {
      setErrorMsg('that needs to be the PDF your Kindle sends');
      return;
    }
    setUploading(true);
    setErrorMsg('');
    setUploadResult(null);
    const fd = new FormData();
    fd.append('file', file);
    axios.post(`${API}/novels/${id}/highlights/import?token=${token}`, fd)
      .then(res => {
        setUploadResult(res.data);
        return reloadHighlights();
      })
      .catch(err => setErrorMsg(`couldn't read that PDF: ${describeError(err)}`))
      .finally(() => setUploading(false));
  };

  // ---- recording ----
  const releaseMic = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  };

  const startRecording = async (target = 'note') => {
    setErrorMsg('');
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setErrorMsg("this browser can't record audio — type it instead");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      // Chrome records webm, iOS Safari records mp4 — take whichever works
      const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
      const mime = candidates.find(t => MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(t)) || '';
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      audioChunksRef.current = [];
      rec.ondataavailable = e => { if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data); };
      rec.onstop = () => handleRecordingDone(rec.mimeType || mime);
      rec.start();
      recorderRef.current = rec;
      recTargetRef.current = target;
      setRecTarget(target);
      setRecSeconds(0);
      setRecState('recording');
      timerRef.current = setInterval(() => setRecSeconds(sec => sec + 1), 1000);
    } catch (err) {
      releaseMic();
      setErrorMsg(`couldn't use the microphone: ${err.message || err}`);
    }
  };

  const stopRecording = () => {
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') rec.stop();
    releaseMic();
  };

  const handleRecordingDone = (mime) => {
    const target = recTargetRef.current;
    const blob = new Blob(audioChunksRef.current, { type: mime || 'audio/webm' });
    if (!blob.size) {
      setRecState('idle');
      setErrorMsg('nothing was recorded — try again');
      return;
    }
    const format = (mime || '').includes('mp4') ? 'mp4' : (mime || '').includes('ogg') ? 'ogg' : 'webm';
    const hl = target !== 'note' ? highlights.find(h => h.id === target) : null;
    setRecState('transcribing');
    blobToBase64(blob)
      .then(b64 => axios.post(`${API}/transcribe`, { token, audio_base64: b64, audio_format: format }))
      .then(res => {
        const heard = (res.data.text || '').trim();
        if (!heard) throw new Error("couldn't hear anything — try again");
        if (target === 'note') setRawTranscript(prev => (prev ? `${prev}\n\n${heard}` : heard));
        else setCommentRaw(prev => (prev ? `${prev}\n\n${heard}` : heard));
        setRecState('tidying');
        return axios.post(`${API}/novels/${id}/notes/tidy`, {
          token, text: heard, chapter_id: (hl ? hl.chapter_id : draftChapterRef.current) || null,
        })
          .then(r => ({ text: (r.data.text || '').trim() || heard, failed: false }))
          .catch(() => ({ text: heard, failed: true }));
      })
      .then(result => {
        if (!result) return;
        // a second recording adds to what's there rather than replacing it
        const add = (prev) => (prev.trim() ? `${prev.trimEnd()}\n\n${result.text}` : result.text);
        if (target === 'note') setDraft(add);
        else setCommentDraft(add);
        if (result.failed) {
          setErrorMsg("the tidy-up didn't work, so this is the raw transcript — edit it or tap tidy to try again");
        }
      })
      .catch(err => setErrorMsg(`transcription failed: ${describeError(err)}`))
      .finally(() => setRecState('idle'));
  };

  // tidy whatever is in the draft right now (typed text, or a re-try)
  const tidyDraft = () => {
    if (!draft.trim()) return;
    setTidyingDraft(true);
    setErrorMsg('');
    axios.post(`${API}/novels/${id}/notes/tidy`, {
      token, text: draft, chapter_id: draftChapterId || null,
    })
      .then(res => { if (res.data.text) setDraft(res.data.text); })
      .catch(err => setErrorMsg(`couldn't tidy it: ${describeError(err)}`))
      .finally(() => setTidyingDraft(false));
  };

  const clearDraft = () => {
    setDraft('');
    setRawTranscript('');
    setShowDraftRaw(false);
  };

  const saveNote = () => {
    if (!draft.trim()) return;
    setSavingNote(true);
    setErrorMsg('');
    axios.post(`${API}/novels/${id}/notes`, {
      token,
      content: draft,
      raw_transcript: rawTranscript || null,
      chapter_id: draftChapterId || null,
    })
      .then(res => {
        setNotes(prev => [res.data.note, ...prev]);
        clearDraft();
      })
      .catch(err => setErrorMsg(`couldn't save the note: ${describeError(err)}`))
      .finally(() => setSavingNote(false));
  };

  // ---- editing saved notes ----
  const startEdit = (n) => {
    setEditingId(n.id);
    setEditDraft(n.content);
    setEditChapterId(n.chapter_id || '');
    setConfirmDeleteId('');
  };

  const saveEdit = () => {
    if (!editDraft.trim()) return;
    setSavingEdit(true);
    setErrorMsg('');
    axios.put(`${API}/novels/${id}/notes/${editingId}`, {
      token, content: editDraft, chapter_id: editChapterId,
    })
      .then(res => {
        setNotes(prev => prev.map(n => (n.id === editingId ? res.data.note : n)));
        setEditingId('');
      })
      .catch(err => setErrorMsg(`couldn't save the edit: ${describeError(err)}`))
      .finally(() => setSavingEdit(false));
  };

  const deleteNote = (noteId) => {
    axios.delete(`${API}/novels/${id}/notes/${noteId}?token=${token}`)
      .then(() => {
        setNotes(prev => prev.filter(n => n.id !== noteId));
        setConfirmDeleteId('');
      })
      .catch(err => setErrorMsg(`couldn't delete the note: ${describeError(err)}`));
  };

  const toggleRaw = (key) =>
    setOpenRawIds(prev => (prev.includes(key) ? prev.filter(x => x !== key) : [...prev, key]));

  // ---- highlights: comments, placing, deleting ----
  const openComment = (hid) => {
    setCommentOn(commentOn === hid ? '' : hid);
    setCommentDraft('');
    setCommentRaw('');
  };

  const saveComment = () => {
    if (!commentDraft.trim() || !commentOn) return;
    setSavingComment(true);
    setErrorMsg('');
    axios.post(`${API}/novels/${id}/highlights/${commentOn}/comments`, {
      token, content: commentDraft, raw_transcript: commentRaw || null,
    })
      .then(res => {
        const c = res.data.comment;
        setHighlights(prev => prev.map(h => (h.id === commentOn ? { ...h, comments: [...(h.comments || []), c] } : h)));
        setCommentOn('');
        setCommentDraft('');
        setCommentRaw('');
      })
      .catch(err => setErrorMsg(`couldn't save the comment: ${describeError(err)}`))
      .finally(() => setSavingComment(false));
  };

  const deleteComment = (hid, cid) => {
    axios.delete(`${API}/novels/${id}/highlights/${hid}/comments/${cid}?token=${token}`)
      .then(() => {
        setHighlights(prev => prev.map(h => (h.id === hid ? { ...h, comments: h.comments.filter(c => c.id !== cid) } : h)));
        setConfirmDeleteId('');
      })
      .catch(err => setErrorMsg(`couldn't delete the comment: ${describeError(err)}`));
  };

  const placeHighlight = (hid, chapterId) => {
    axios.put(`${API}/novels/${id}/highlights/${hid}`, { token, chapter_id: chapterId || null })
      .then(res => setHighlights(prev => prev.map(h => (h.id === hid ? { ...h, ...res.data.highlight } : h))))
      .catch(err => setErrorMsg(`couldn't move the highlight: ${describeError(err)}`));
  };

  const deleteHighlight = (hid) => {
    axios.delete(`${API}/novels/${id}/highlights/${hid}?token=${token}`)
      .then(() => {
        setHighlights(prev => prev.filter(h => h.id !== hid));
        setConfirmDeleteId('');
      })
      .catch(err => setErrorMsg(`couldn't delete the highlight: ${describeError(err)}`));
  };

  const busy = recState !== 'idle';
  const mmss = `${Math.floor(recSeconds / 60)}:${String(recSeconds % 60).padStart(2, '0')}`;
  const stoppedAt = novel?.stopped_chapter_id
    ? chapters.find(c => c.id === novel.stopped_chapter_id)?.title
    : null;
  const chapterTitle = (cid) => {
    const c = chapters.find(x => x.id === cid);
    return c ? (c.title || `chapter ${c.order}`) : null;
  };
  const unplaced = highlights.filter(h => !h.chapter_id);
  const showHl = show !== 'notes';
  const showNotes = show !== 'highlights';

  const s = {
    page: {
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: '#0e0b0f', fontFamily: "'DM Sans', sans-serif", fontWeight: 300,
      color: '#e8ddd0', display: 'flex', flexDirection: 'column', overflowY: 'hidden',
    },
    header: {
      padding: '16px 24px', display: 'flex', alignItems: 'center', gap: '12px',
      borderBottom: '1px solid rgba(201,147,90,0.06)',
      background: 'linear-gradient(180deg, #120f18 0%, #0e0b0f 100%)', flexShrink: 0,
    },
    backBtn: {
      background: 'transparent', border: 'none', cursor: 'pointer', color: '#4a3d30',
      fontSize: '13px', fontFamily: "'DM Sans', sans-serif", flexShrink: 0,
    },
    headerTitle: {
      flex: 1, minWidth: 0, textAlign: 'center',
      fontFamily: "'Cormorant Garamond', serif", fontSize: '18px', fontStyle: 'italic',
      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
    },
    headerCount: { fontSize: '11px', color: '#4a3d30', flexShrink: 0, whiteSpace: 'nowrap' },
    // the whole width scrolls (so you can swipe on the sides on an iPad),
    // while the content itself stays the same 680px column in the middle
    body: {
      flex: 1, overflowY: 'auto', overflowX: 'hidden', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain',
      paddingTop: '20px', paddingBottom: '80px',
      paddingLeft: 'max(20px, calc((100% - 640px) / 2))', paddingRight: 'max(20px, calc((100% - 640px) / 2))',
      width: '100%', boxSizing: 'border-box',
    },
    statusLine: {
      display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px',
      fontSize: '12px', color: '#8a7466', marginBottom: '16px',
    },
    statusChip: (status) => ({
      padding: '3px 10px', borderRadius: '12px', fontSize: '11px',
      background: status === 'dropped' ? 'rgba(200,106,106,0.14)'
        : status === 'finished' ? 'rgba(127,199,154,0.12)'
        : 'rgba(201,147,90,0.14)',
      color: status === 'dropped' ? '#e0a0a0' : status === 'finished' ? '#9fd4b1' : '#c9935a',
    }),
    linkBtn: {
      background: 'transparent', border: 'none', color: '#c9935a', fontSize: '12px',
      cursor: 'pointer', padding: 0, fontFamily: "'DM Sans', sans-serif",
    },
    composer: {
      background: '#1e1825', border: '1px solid rgba(201,147,90,0.12)',
      borderRadius: '18px', padding: '16px', marginBottom: '16px',
      display: 'flex', flexDirection: 'column', gap: '12px',
    },
    composerTop: { display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' },
    micBtn: (recording) => ({
      width: '56px', height: '56px', borderRadius: '50%', border: 'none', flexShrink: 0,
      background: recording ? '#b04a4a' : 'linear-gradient(135deg, #8a5f35, #c9935a)',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      cursor: 'pointer', boxShadow: recording ? '0 0 0 6px rgba(176,74,74,0.18)' : 'none',
    }),
    micSmall: (recording) => ({
      width: '38px', height: '38px', borderRadius: '50%', border: 'none', flexShrink: 0,
      background: recording ? '#b04a4a' : 'linear-gradient(135deg, #8a5f35, #c9935a)',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
    }),
    micHint: { flex: 1, minWidth: '140px', fontSize: '12px', color: '#8a7466', lineHeight: 1.5 },
    micHintStrong: { color: '#e8ddd0' },
    select: {
      background: '#120f18', border: '1px solid rgba(201,147,90,0.15)',
      borderRadius: '10px', padding: '8px 10px', color: '#e8ddd0',
      fontSize: '12px', fontFamily: "'DM Sans', sans-serif", outline: 'none',
      maxWidth: '100%',
    },
    label: { fontSize: '10px', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#4a3d30' },
    textarea: {
      width: '100%', minHeight: '130px', boxSizing: 'border-box', resize: 'vertical',
      background: '#120f18', border: '1px solid rgba(201,147,90,0.12)',
      borderRadius: '12px', padding: '12px 14px', color: '#e8ddd0',
      fontSize: '14px', lineHeight: 1.7, fontFamily: "'DM Sans', sans-serif",
      fontWeight: 300, outline: 'none',
    },
    rawBox: {
      background: '#120f18', border: '1px dashed rgba(201,147,90,0.15)',
      borderRadius: '10px', padding: '10px 12px', fontSize: '12px',
      color: '#8a7466', lineHeight: 1.6, whiteSpace: 'pre-wrap',
    },
    row: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' },
    btnFilled: {
      padding: '8px 18px', borderRadius: '20px', border: 'none',
      background: 'linear-gradient(135deg, #8a5f35, #c9935a)', color: '#0e0b0f',
      fontSize: '12px', cursor: 'pointer', fontWeight: 400, fontFamily: "'DM Sans', sans-serif",
    },
    btnFilledDisabled: {
      padding: '8px 18px', borderRadius: '20px', border: 'none',
      background: 'rgba(201,147,90,0.15)', color: 'rgba(201,147,90,0.45)',
      fontSize: '12px', fontWeight: 400, cursor: 'default', fontFamily: "'DM Sans', sans-serif",
    },
    btn: {
      padding: '8px 14px', borderRadius: '20px', border: '1px solid rgba(201,147,90,0.2)',
      background: 'transparent', color: '#c9935a', fontSize: '12px', cursor: 'pointer',
      fontFamily: "'DM Sans', sans-serif", fontWeight: 300,
    },
    btnQuiet: {
      padding: '8px 12px', borderRadius: '20px', border: 'none', background: 'transparent',
      color: '#4a3d30', fontSize: '12px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
    },
    errorBanner: {
      background: 'rgba(180,60,60,0.12)', border: '1px solid rgba(220,100,100,0.3)',
      borderRadius: '12px', padding: '10px 14px', marginBottom: '16px',
      fontSize: '12px', color: '#e0a0a0', wordBreak: 'break-word',
    },
    kindleBar: {
      background: '#17131b', border: '1px solid rgba(201,147,90,0.1)', borderRadius: '16px',
      padding: '12px 14px', marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '8px',
    },
    kindleTop: { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' },
    kindleText: { flex: 1, minWidth: '160px', fontSize: '12px', color: '#8a7466', lineHeight: 1.5 },
    uploadBtn: {
      display: 'inline-flex', alignItems: 'center', gap: '7px',
      padding: '8px 14px', borderRadius: '20px', border: 'none',
      background: 'linear-gradient(135deg, #8a5f35, #c9935a)', color: '#0e0b0f',
      fontSize: '12px', cursor: 'pointer', fontWeight: 400, fontFamily: "'DM Sans', sans-serif",
    },
    resultBox: {
      fontSize: '12px', color: '#c9a57a', lineHeight: 1.6, padding: '8px 10px',
      borderRadius: '10px', background: 'rgba(201,147,90,0.07)',
    },
    importRow: { display: 'flex', justifyContent: 'space-between', gap: '10px', fontSize: '11.5px', color: '#8a7466', padding: '4px 0' },
    toolbar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', marginBottom: '18px' },
    seg: { display: 'flex', background: '#1e1825', borderRadius: '20px', padding: '3px', border: '1px solid rgba(201,147,90,0.1)' },
    segBtn: (on) => ({
      padding: '6px 14px', borderRadius: '16px', border: 'none',
      background: on ? 'linear-gradient(135deg, #8a5f35, #c9935a)' : 'transparent',
      color: on ? '#0e0b0f' : '#8a7466', fontSize: '12px', cursor: 'pointer',
      fontWeight: on ? 400 : 300, fontFamily: "'DM Sans', sans-serif",
    }),
    filterBtn: (on) => ({
      padding: '5px 10px', borderRadius: '14px', border: 'none', background: 'transparent',
      color: on ? '#c9935a' : '#4a3d30', fontSize: '12px', cursor: 'pointer',
      fontFamily: "'DM Sans', sans-serif", textDecoration: on ? 'underline' : 'none', textUnderlineOffset: '4px',
    }),
    groupHeader: {
      display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '10px',
      fontFamily: "'Cormorant Garamond', serif", fontSize: '21px', fontStyle: 'italic', color: '#e8ddd0',
      margin: '0 0 10px', paddingBottom: '6px', borderBottom: '1px solid rgba(201,147,90,0.1)',
    },
    groupCount: { fontFamily: "'DM Sans', sans-serif", fontStyle: 'normal', fontSize: '11px', color: '#4a3d30' },
    dayHeader: {
      fontSize: '11px', letterSpacing: '0.1em', textTransform: 'uppercase',
      color: '#8a7466', margin: '0 0 10px', paddingBottom: '6px',
      borderBottom: '1px solid rgba(201,147,90,0.08)',
    },
    day: { marginBottom: '26px' },
    note: {
      padding: '12px 0 14px', borderBottom: '1px solid rgba(201,147,90,0.05)',
      display: 'flex', flexDirection: 'column', gap: '8px',
    },
    noteMeta: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', fontSize: '11px' },
    kindTag: { display: 'inline-flex', alignItems: 'center', gap: '5px', color: '#8a7466', fontSize: '11px' },
    noteTime: { color: '#c9935a', fontVariantNumeric: 'tabular-nums' },
    chapterChip: {
      padding: '2px 8px', borderRadius: '10px', background: 'rgba(201,147,90,0.1)',
      color: '#c9a57a', fontSize: '11px', maxWidth: '100%',
      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    },
    editedTag: { color: '#4a3d30', fontSize: '11px' },
    noteBody: { fontSize: '14px', lineHeight: 1.75, whiteSpace: 'pre-wrap', color: '#e8ddd0' },
    noteActions: { display: 'flex', gap: '14px', flexWrap: 'wrap', alignItems: 'center' },
    noteAction: {
      background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
      color: '#4a3d30', fontSize: '11px', fontFamily: "'DM Sans', sans-serif",
    },
    noteActionDanger: {
      background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
      color: '#e0a0a0', fontSize: '11px', fontFamily: "'DM Sans', sans-serif",
    },
    highlight: {
      padding: '14px 0 16px', borderBottom: '1px solid rgba(201,147,90,0.05)',
      display: 'flex', flexDirection: 'column', gap: '9px',
    },
    quote: (color) => ({
      fontFamily: HIGHLIGHT_FONT, fontSize: '16.5px', lineHeight: 1.65, color: '#efe4d6',
      borderLeft: `3px solid ${HIGHLIGHT_COLORS[color] || HIGHLIGHT_COLORS.yellow}`, paddingLeft: '14px',
      whiteSpace: 'pre-wrap',
    }),
    kindleNote: { fontSize: '12.5px', color: '#c9a57a', paddingLeft: '17px', lineHeight: 1.6 },
    comments: { display: 'flex', flexDirection: 'column', gap: '8px', paddingLeft: '17px' },
    comment: { background: '#17131b', borderRadius: '12px', padding: '9px 12px', display: 'flex', flexDirection: 'column', gap: '5px' },
    commentMeta: { fontSize: '10.5px', color: '#6d5d4e' },
    commentBody: { fontSize: '13.5px', lineHeight: 1.65, whiteSpace: 'pre-wrap' },
    commentBox: {
      marginLeft: '17px', background: '#1e1825', border: '1px solid rgba(201,147,90,0.14)',
      borderRadius: '14px', padding: '10px', display: 'flex', flexDirection: 'column', gap: '8px',
    },
    commentArea: {
      width: '100%', minHeight: '70px', boxSizing: 'border-box', resize: 'vertical',
      background: '#120f18', border: '1px solid rgba(201,147,90,0.12)', borderRadius: '10px',
      padding: '9px 11px', color: '#e8ddd0', fontSize: '13.5px', lineHeight: 1.6,
      fontFamily: "'DM Sans', sans-serif", fontWeight: 300, outline: 'none',
    },
    quoteSnippet: {
      fontFamily: HIGHLIGHT_FONT, fontStyle: 'italic', fontSize: '13.5px', color: '#a8927f',
      borderLeft: '2px solid rgba(201,147,90,0.3)', paddingLeft: '10px',
      display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
    },
    unplacedBox: {
      background: 'rgba(201,147,90,0.06)', border: '1px solid rgba(201,147,90,0.25)', borderRadius: '16px',
      padding: '12px 14px', marginBottom: '22px',
    },
    empty: {
      textAlign: 'center', color: '#4a3d30', fontSize: '13px', lineHeight: 1.8, padding: '24px 12px',
    },
  };

  const chapterOptions = (
    <>
      <option value="">whole book (no chapter)</option>
      {chapters.map(c => (
        <option key={c.id} value={c.id}>{c.title || `chapter ${c.order}`}</option>
      ))}
    </>
  );

  // ---------- one note ----------
  const renderNote = (n, { showChapter = true, showDay = false } = {}) => (
    <div key={`n-${n.id}`} style={s.note}>
      <div style={s.noteMeta}>
        <span style={s.kindTag}><NoteIcon /> note</span>
        <span style={s.noteTime}>{showDay ? `${shortDate(n.created_at)} · ` : ''}{timeLabel(n.created_at)}</span>
        {showChapter && <span style={s.chapterChip}>{n.chapter_title || 'whole book'}</span>}
        {n.edited && <span style={s.editedTag}>edited</span>}
      </div>

      {editingId === n.id ? (
        <>
          <select style={{ ...s.select, alignSelf: 'flex-start' }} value={editChapterId} onChange={e => setEditChapterId(e.target.value)}>
            {chapterOptions}
          </select>
          <textarea style={s.textarea} value={editDraft} autoFocus onChange={e => setEditDraft(e.target.value)} />
          <div style={s.row}>
            <button style={editDraft.trim() && !savingEdit ? s.btnFilled : s.btnFilledDisabled} onClick={saveEdit} disabled={!editDraft.trim() || savingEdit}>
              {savingEdit ? 'saving…' : 'save'}
            </button>
            <button style={s.btnQuiet} onClick={() => setEditingId('')}>cancel</button>
          </div>
        </>
      ) : (
        <>
          <div style={s.noteBody}>{n.content}</div>
          {n.raw_transcript && openRawIds.includes(`n-${n.id}`) && <div style={s.rawBox}>{n.raw_transcript}</div>}
          <div style={s.noteActions}>
            <button style={s.noteAction} onClick={() => startEdit(n)}>edit</button>
            {n.raw_transcript && (
              <button style={s.noteAction} onClick={() => toggleRaw(`n-${n.id}`)}>
                {openRawIds.includes(`n-${n.id}`) ? 'hide original' : 'original'}
              </button>
            )}
            {confirmDeleteId === `n-${n.id}` ? (
              <>
                <span style={{ fontSize: '11px', color: '#8a7466' }}>delete this note?</span>
                <button style={s.noteActionDanger} onClick={() => deleteNote(n.id)}>delete</button>
                <button style={s.noteAction} onClick={() => setConfirmDeleteId('')}>keep</button>
              </>
            ) : (
              <button style={s.noteAction} onClick={() => setConfirmDeleteId(`n-${n.id}`)}>delete</button>
            )}
          </div>
        </>
      )}
    </div>
  );

  // ---------- the comment box under a highlight ----------
  const renderCommentBox = (h) => {
    const recHere = recTarget === h.id && recState !== 'idle';
    return (
      <div style={s.commentBox}>
        <div style={s.row}>
          <button
            style={s.micSmall(recHere && recState === 'recording')}
            onClick={recHere && recState === 'recording' ? stopRecording : () => startRecording(h.id)}
            disabled={busy && !recHere}
            aria-label={recHere && recState === 'recording' ? 'stop recording' : 'say your comment'}
          >
            {recHere && recState === 'recording' ? <StopIcon size={14} /> : <MicIcon size={17} />}
          </button>
          <span style={{ fontSize: '12px', color: '#8a7466' }}>
            {recHere && recState === 'recording' && <span style={s.micHintStrong}>recording {mmss} — tap to stop</span>}
            {recHere && recState === 'transcribing' && <span style={s.micHintStrong}>writing down what you said…</span>}
            {recHere && recState === 'tidying' && <span style={s.micHintStrong}>tidying it up…</span>}
            {!recHere && 'say it, or type below'}
          </span>
        </div>
        <textarea style={s.commentArea} value={commentDraft} autoFocus onChange={e => setCommentDraft(e.target.value)}
          placeholder="what you think about this line…" disabled={recHere} />
        <div style={s.row}>
          <button style={commentDraft.trim() && !savingComment && !recHere ? s.btnFilled : s.btnFilledDisabled}
            onClick={saveComment} disabled={!commentDraft.trim() || savingComment || recHere}>
            {savingComment ? 'saving…' : 'save comment'}
          </button>
          <button style={s.btnQuiet} onClick={() => openComment(h.id)} disabled={recHere}>cancel</button>
        </div>
      </div>
    );
  };

  // ---------- one highlight ----------
  const renderHighlight = (h, { showChapter = true } = {}) => (
    <div key={`h-${h.id}`} style={s.highlight}>
      <div style={s.noteMeta}>
        <span style={s.kindTag}>{h.kind === 'note' ? <><NoteIcon /> kindle note</> : <><QuoteIcon /> highlight</>}</span>
        {h.page != null && <span style={s.noteTime}>p. {h.page}</span>}
        {showChapter && <span style={s.chapterChip}>{h.chapter_id ? (chapterTitle(h.chapter_id) || h.chapter_title) : 'no chapter'}</span>}
        {h.highlighted_at && <span style={s.editedTag}>highlighted {shortDate(h.highlighted_at)}</span>}
      </div>
      {h.kind === 'note'
        ? <div style={{ ...s.noteBody, paddingLeft: '17px' }}>{h.text}</div>
        : <div style={s.quote(h.color)}>{h.text}</div>}
      {h.kindle_note && <div style={s.kindleNote}>your note on the kindle: {h.kindle_note}</div>}

      {(h.comments || []).length > 0 && (
        <div style={s.comments}>
          {h.comments.map(c => (
            <div key={c.id} style={s.comment}>
              <div style={s.commentMeta}>{shortDate(c.created_at)} · {timeLabel(c.created_at)}</div>
              <div style={s.commentBody}>{c.content}</div>
              {c.raw_transcript && openRawIds.includes(`c-${c.id}`) && <div style={s.rawBox}>{c.raw_transcript}</div>}
              <div style={s.noteActions}>
                {c.raw_transcript && (
                  <button style={s.noteAction} onClick={() => toggleRaw(`c-${c.id}`)}>{openRawIds.includes(`c-${c.id}`) ? 'hide original' : 'original'}</button>
                )}
                {confirmDeleteId === `c-${c.id}` ? (
                  <>
                    <button style={s.noteActionDanger} onClick={() => deleteComment(h.id, c.id)}>delete</button>
                    <button style={s.noteAction} onClick={() => setConfirmDeleteId('')}>keep</button>
                  </>
                ) : (
                  <button style={s.noteAction} onClick={() => setConfirmDeleteId(`c-${c.id}`)}>delete</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {commentOn === h.id && renderCommentBox(h)}

      <div style={{ ...s.noteActions, paddingLeft: '17px' }}>
        {commentOn !== h.id && (
          <button style={{ ...s.noteAction, color: '#c9935a' }} onClick={() => openComment(h.id)}>
            <CommentIcon size={11} /> {(h.comments || []).length ? 'add a comment' : 'comment'}
          </button>
        )}
        {h.chapter_id && (
          <button style={s.noteAction} onClick={() => navigate(`/novels/${id}/chapters/${h.chapter_id}`)}>open chapter</button>
        )}
        <select style={{ ...s.select, padding: '3px 6px', fontSize: '11px', color: '#8a7466', background: 'transparent', border: 'none' }}
          value={h.chapter_id || ''} onChange={e => placeHighlight(h.id, e.target.value)} aria-label="move to chapter">
          <option value="">no chapter</option>
          {chapters.map(c => <option key={c.id} value={c.id}>{c.title || `chapter ${c.order}`}</option>)}
        </select>
        {confirmDeleteId === `h-${h.id}` ? (
          <>
            <span style={{ fontSize: '11px', color: '#8a7466' }}>delete this highlight{(h.comments || []).length ? ' and its comments' : ''}?</span>
            <button style={s.noteActionDanger} onClick={() => deleteHighlight(h.id)}>delete</button>
            <button style={s.noteAction} onClick={() => setConfirmDeleteId('')}>keep</button>
          </>
        ) : (
          <button style={s.noteAction} onClick={() => setConfirmDeleteId(`h-${h.id}`)}>delete</button>
        )}
      </div>
    </div>
  );

  // ---------- by chapter: story order ----------
  const byChapter = () => {
    const groups = [];
    const order = [...chapters.map(c => c.id), ''];
    order.forEach(cid => {
      const hs = showHl ? highlights.filter(h => (h.chapter_id || '') === cid && (cid || false)).sort((a, b) => (a.page ?? 1e9) - (b.page ?? 1e9)) : [];
      const ns = showNotes ? notes.filter(n => (n.chapter_id || '') === cid) : [];
      if (!hs.length && !ns.length) return;
      groups.push({ cid, title: cid ? chapterTitle(cid) : 'the whole book', hs, ns });
    });
    if (!groups.length && !(showHl && unplaced.length)) {
      return <div style={s.empty}>nothing here yet.<br />upload your kindle highlights or record a note, and it'll be filed by chapter.</div>;
    }
    return (
      <>
        {showHl && unplaced.length > 0 && (
          <div style={s.unplacedBox}>
            <div style={{ ...s.groupHeader, borderBottom: 'none', marginBottom: '2px' }}>
              <span>not matched to a chapter</span><span style={s.groupCount}>{unplaced.length}</span>
            </div>
            <div style={{ fontSize: '12px', color: '#8a7466', marginBottom: '6px' }}>pick a chapter for each one with the menu under it.</div>
            {unplaced.map(h => renderHighlight(h, { showChapter: false }))}
          </div>
        )}
        {groups.map(g => (
          <div key={g.cid || 'book'} style={s.day}>
            <div style={s.groupHeader}>
              <span>{g.title}</span>
              <span style={s.groupCount}>
                {[g.hs.length ? `${g.hs.length} highlight${g.hs.length === 1 ? '' : 's'}` : '', g.ns.length ? `${g.ns.length} note${g.ns.length === 1 ? '' : 's'}` : ''].filter(Boolean).join(' · ')}
              </span>
            </div>
            {g.hs.map(h => renderHighlight(h, { showChapter: false }))}
            {g.ns.map(n => renderNote(n, { showChapter: false, showDay: true }))}
          </div>
        ))}
      </>
    );
  };

  // ---------- timeline: everything by date, newest first ----------
  const timeline = () => {
    const items = [];
    if (showNotes) notes.forEach(n => items.push({ kind: 'note', at: n.created_at, n }));
    if (showHl) {
      highlights.forEach(h => {
        items.push({ kind: 'highlight', at: h.highlighted_at || h.created_at, h });
        (h.comments || []).forEach(c => items.push({ kind: 'comment', at: c.created_at, h, c }));
      });
    }
    // a highlight carries only a day, so on the same day it sits below that day's timed entries
    const sortKey = (it) => (/^\d{4}-\d{2}-\d{2}$/.test(it.at || '') ? toDate(it.at).getTime() - 12 * 3600e3 : new Date(it.at).getTime());
    items.sort((a, b) => sortKey(b) - sortKey(a));
    if (!items.length) return <div style={s.empty}>nothing here yet.</div>;
    const days = [];
    items.forEach(it => {
      const key = dayKey(it.at);
      const last = days[days.length - 1];
      if (last && last.key === key) last.items.push(it);
      else days.push({ key, label: dayLabel(it.at), items: [it] });
    });
    return days.map(day => (
      <div key={day.key} style={s.day}>
        <div style={s.dayHeader}>{day.label}</div>
        {day.items.map(it => {
          if (it.kind === 'note') return renderNote(it.n);
          if (it.kind === 'highlight') return renderHighlight(it.h);
          return (
            <div key={`c-${it.c.id}`} style={s.note}>
              <div style={s.noteMeta}>
                <span style={s.kindTag}><CommentIcon /> comment</span>
                <span style={s.noteTime}>{timeLabel(it.c.created_at)}</span>
                <span style={s.chapterChip}>{it.h.chapter_id ? chapterTitle(it.h.chapter_id) : 'no chapter'}</span>
              </div>
              <div style={s.quoteSnippet}>{it.h.text}</div>
              <div style={s.noteBody}>{it.c.content}</div>
            </div>
          );
        })}
      </div>
    ));
  };

  if (loading) return <div style={s.page} />;

  const counts = [
    highlights.length ? `${highlights.length} highlight${highlights.length === 1 ? '' : 's'}` : '',
    `${notes.length} note${notes.length === 1 ? '' : 's'}`,
  ].filter(Boolean).join(' · ');

  return (
    <div style={s.page}>
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=DM+Sans:wght@300;400&display=swap" rel="stylesheet" />

      <header style={s.header}>
        <button style={s.backBtn} onClick={() => navigate(`/novels/${id}`)}>← back</button>
        <div style={s.headerTitle}>journal · {novel?.title || 'novel'}</div>
        <span style={s.headerCount}>{counts}</span>
      </header>

      <div style={s.body}>
        {/* where you are with this book */}
        <div style={s.statusLine}>
          {novel?.reading_status
            ? <span style={s.statusChip(novel.reading_status)}>{STATUS_LABELS[novel.reading_status] || novel.reading_status}</span>
            : <span>no reading status yet</span>}
          {novel?.started_month && <span>started {formatMonth(novel.started_month)}</span>}
          {novel?.finished_month && (
            <span>· {novel.reading_status === 'dropped' ? 'dropped' : 'finished'} {formatMonth(novel.finished_month)}</span>
          )}
          {stoppedAt && <span>· at {stoppedAt}</span>}
          <button style={s.linkBtn} onClick={() => navigate(`/novels/${id}`)}>change</button>
        </div>
        {novel?.reading_status === 'dropped' && novel?.drop_reason && (
          <div style={{ ...s.statusLine, marginTop: '-8px' }}>why: {novel.drop_reason}</div>
        )}

        {errorMsg && <div style={s.errorBanner} onClick={() => setErrorMsg('')}>⚠ {errorMsg}</div>}

        {/* READING TIMER — the same one as on the study page, counting toward this book */}
        <ReadingTimer kind="novel" id={id} title={novel?.title} />

        {/* NEW NOTE */}
        <div style={s.composer}>
          <div style={s.composerTop}>
            <button
              style={s.micBtn(recTarget === 'note' && recState === 'recording')}
              onClick={recTarget === 'note' && recState === 'recording' ? stopRecording : () => startRecording('note')}
              disabled={busy && !(recTarget === 'note' && recState === 'recording')}
              title={recState === 'recording' ? 'stop recording' : 'record a voice note'}
            >
              {recTarget === 'note' && recState === 'recording' ? <StopIcon /> : <MicIcon />}
            </button>
            <div style={s.micHint}>
              {recTarget === 'note' && recState === 'recording' && <span style={s.micHintStrong}>recording {mmss} — tap to stop</span>}
              {recTarget === 'note' && recState === 'transcribing' && <span style={s.micHintStrong}>writing down what you said…</span>}
              {recTarget === 'note' && recState === 'tidying' && <span style={s.micHintStrong}>tidying it up…</span>}
              {(recState === 'idle' || recTarget !== 'note') && (draft.trim()
                ? 'tap again to add more, or edit below and save'
                : 'tap and talk — what you think so far, where you are, what you expect. or just type below.')}
            </div>
          </div>

          <div style={s.row}>
            <span style={s.label}>about</span>
            <select style={s.select} value={draftChapterId} onChange={e => setDraftChapterId(e.target.value)}>
              {chapterOptions}
            </select>
          </div>

          <textarea
            style={s.textarea}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            placeholder="your note…"
            disabled={recTarget === 'note' && busy}
          />

          {rawTranscript && (
            <>
              <button style={{ ...s.linkBtn, alignSelf: 'flex-start', color: '#8a7466' }} onClick={() => setShowDraftRaw(v => !v)}>
                {showDraftRaw ? 'hide what you actually said' : 'show what you actually said'}
              </button>
              {showDraftRaw && <div style={s.rawBox}>{rawTranscript}</div>}
            </>
          )}

          <div style={s.row}>
            <button
              style={draft.trim() && !busy && !savingNote ? s.btnFilled : s.btnFilledDisabled}
              onClick={saveNote}
              disabled={!draft.trim() || busy || savingNote}
            >
              {savingNote ? 'saving…' : 'save note'}
            </button>
            {draft.trim() && (
              <button style={s.btn} onClick={tidyDraft} disabled={busy || tidyingDraft} title="rewrite it as clean first person">
                {tidyingDraft ? 'tidying…' : '✨ tidy'}
              </button>
            )}
            {(draft || rawTranscript) && (
              <button style={s.btnQuiet} onClick={clearDraft} disabled={busy}>discard</button>
            )}
          </div>
        </div>

        {/* KINDLE HIGHLIGHTS */}
        <div style={s.kindleBar}>
          <div style={s.kindleTop}>
            <div style={s.kindleText}>
              {highlights.length
                ? <>kindle highlights: <span style={s.micHintStrong}>{highlights.length}</span>{imports.length ? ` · last upload ${shortDate(imports[0].created_at)}` : ''}</>
                : 'export your notebook from the kindle and upload the PDF here — each highlight is filed under its chapter.'}
            </div>
            <input ref={fileRef} type="file" accept="application/pdf,.pdf" style={{ display: 'none' }} onChange={onPickFile} />
            <button style={s.uploadBtn} onClick={() => fileRef.current?.click()} disabled={uploading}>
              <UploadIcon /> {uploading ? 'reading it…' : 'upload highlights'}
            </button>
          </div>
          {uploadResult && (
            <div style={s.resultBox} role="status">
              {uploadResult.added
                ? `added ${uploadResult.added} new highlight${uploadResult.added === 1 ? '' : 's'}`
                : 'nothing new — every highlight in that PDF was already here'}
              {uploadResult.skipped ? ` · ${uploadResult.skipped} already here` : ''}
              {uploadResult.notes_added ? ` · ${uploadResult.notes_added} kindle note${uploadResult.notes_added === 1 ? '' : 's'} added to highlights already here` : ''}
              {uploadResult.unplaced ? ` · ${uploadResult.unplaced} couldn't be matched to a chapter (they're at the top of "by chapter")` : ''}
            </div>
          )}
          {imports.length > 0 && (
            <>
              <button style={{ ...s.linkBtn, alignSelf: 'flex-start', color: '#8a7466', fontSize: '11.5px' }} onClick={() => setShowImports(v => !v)}>
                {showImports ? 'hide uploads' : `${imports.length} upload${imports.length === 1 ? '' : 's'}`}
              </button>
              {showImports && imports.map(im => (
                <div key={im.id} style={s.importRow}>
                  <span>{shortDate(im.created_at)} · {timeLabel(im.created_at)}</span>
                  <span>{im.added} new of {im.total}</span>
                </div>
              ))}
            </>
          )}
        </div>

        {/* THE JOURNAL */}
        <div style={s.toolbar}>
          <div style={s.seg} role="tablist" aria-label="view">
            <button role="tab" aria-selected={view === 'chapter'} style={s.segBtn(view === 'chapter')} onClick={() => pickView('chapter')}>by chapter</button>
            <button role="tab" aria-selected={view === 'timeline'} style={s.segBtn(view === 'timeline')} onClick={() => pickView('timeline')}>timeline</button>
          </div>
          <div style={s.row} role="group" aria-label="show">
            {[['all', 'all'], ['highlights', 'highlights'], ['notes', 'notes']].map(([k, label]) => (
              <button key={k} style={s.filterBtn(show === k)} aria-pressed={show === k} onClick={() => setShow(k)}>{label}</button>
            ))}
          </div>
        </div>

        {view === 'chapter' ? byChapter() : timeline()}
      </div>
    </div>
  );
}
